/**
 * @module SchedulerPro/data/stm/action/EventUpdateAction
 */
import UpdateAction from '../../../../Core/data/stm/action/UpdateAction.js';
/**
 * Action to record the fact that an event model has been updated.
 * @extends Core/data/stm/action/UpdateAction
 */
export default class EventUpdateAction extends UpdateAction {
    get type() {
        return 'EventUpdateAction';
    }
    construct(config) {
        let {
            model,
            newData,
            oldData
        } = config;
        // If we have "segments" represented in both old & new data states
        if (newData.segments && oldData.segments) {
            oldData = { ...oldData };
            newData = { ...newData };
            const
                oldDataSegments = oldData.segments.slice(),
                newDataSegments = newData.segments.slice();
            let hasChanges = false;
            // If a segment instance exists in both states
            // we need to find segments existing in both versions.
            // They should not be changed when undo/redo the main event
            // since their model changes are recorded by STM.
            oldData.segments.forEach((segment, index) => {
                const newDataIndex = newData.segments.indexOf(segment);
                // If a segment instance exists in both states
                // we uses it as-is ..since STM is supposed to handle the instance changes
                if (newDataIndex > -1) {
                    oldDataSegments[index] = newDataSegments[newDataIndex] = [segment];
                    hasChanges = true;
                }
            });
            if (hasChanges) {
                oldData.segments = oldDataSegments;
                newData.segments = newDataSegments;
            }
        }
        if (oldData.segments) {
            // make snapshots for segments that no longer exist in the new version
            oldData.segments = oldData.segments.map(segment => {
                return Array.isArray(segment) ? segment : this.packSegmentSnapshot(segment);
            });
        }
        if (newData.segments) {
            // make snapshots for segments that appeared in the new version
            newData.segments = newData.segments.map(segment => {
                return Array.isArray(segment) ? segment : this.packSegmentSnapshot(segment);
            });
        }
        return super.construct({
            model,
            newData,
            oldData
        });
    }
    // Packs a segment to a snapshot looking as [segment, data] array,
    // where:
    // - segment - the segment instance
    // - data - the segment data
    packSegmentSnapshot(segment) {
        if (segment.isModel) {
            const data = segment.toJSON();
            data.startOffset    = segment.startOffset;
            data.endOffset      = segment.endOffset;
            return [segment, data];
        }
        return [null, segment];
    }
    // Unpacks the provided segment snapshot
    // by applying the snapshot data to the segment and returning the segment
    unpackSegmentSnapshot(segmentSnapshot) {
        const [segment, data] = segmentSnapshot;
        if (segment && !segment.isDestroyed) {
            Object.assign(segment, data);
            return segment;
        }
        return data;
    }
    undo() {
        const { oldData } = this;
        if (oldData.segments) {
            oldData.segments = oldData.segments.map(this.unpackSegmentSnapshot);
        }
        super.undo();
    }
    redo() {
        const { newData } = this;
        if (newData.segments) {
            newData.segments = newData.segments.map(this.unpackSegmentSnapshot);
        }
        super.redo();
    }
}
EventUpdateAction._$name = 'EventUpdateAction';